import { atom } from "jotai";
import { produce } from "immer";
import {
  ApiAssigneeData,
  AssigneeData,
  AssigneeTableData,
  CloneScheduleInput,
  DeleteAssigneeItemInput,
  IncomingAssigneeTableData,
  UpdateAssigneeInput,
} from "../type/assigneeType";
import { v4 as uuid } from "uuid";
import { dayjs } from "utilz/dayjs";
// parent data

/**
 * @description don't update it directly. uses write only fns like updateAssignee
 * @description use read only fuction assigneeList to read data
 */
const apiAssigneesAtom = atom<ApiAssigneeData>({
  new: [],
  updated: [],
  deleted: [],
});
const assigneesAtom = atom<AssigneeTableData[]>([]);
const selectedResourceAtom = atom<Array<string>>([]);

const customerIdAtom = atom<string | undefined>(undefined);
const serviceTypeAtom = atom<"tickets" | "tasks" | "projects" | undefined>(
  undefined,
);
// action functions
export const selectedCustomerId = atom((get) => get(customerIdAtom));
export const selectedServiceType = atom((get) => get(serviceTypeAtom));
export const assigneeList = atom((get) => get(assigneesAtom));
export const apiAssignees = atom((get) => get(apiAssigneesAtom));

export const selectedResourceList = atom((get) => get(selectedResourceAtom));

/**
 * @description fn used to add new assignee from resource list
 */

export const setCustomerIdAndServiceType = atom(
  null,
  (
    get,
    set,
    {
      customerId,
      serviceType,
    }: { customerId: string; serviceType: "tickets" | "tasks" | "projects" },
  ) => {
    set(customerIdAtom, customerId);
    set(serviceTypeAtom, serviceType);
  },
);

export const addAssignee = atom(null, (get, set, value: AssigneeTableData) => {
  set(assigneesAtom, [...get(assigneesAtom), value]);
  set(selectedResourceAtom, [...get(selectedResourceAtom), value.resource_id]);
  set(apiAssigneesAtom, (prev) => {
    const newData = produce(prev, (draft) => {
      draft.new.push({
        ...value,
        schedule_times: [
          { schedule_uuid: value.uuid, start_time: null, end_time: null },
        ],
      });
    });
    return newData;
  });
});

export const setAssigneeList = atom(
  null,
  (get, set, value: IncomingAssigneeTableData[]) => {
    const tableData: Array<AssigneeTableData> = [];
    let apiNewData: Array<AssigneeData> = get(apiAssigneesAtom)?.new || []; // Use existing data if available

    value.forEach((assignee) => {
      const uid = uuid(); // Generate a unique ID for the assignee if no mapping_id exists

      // Build table data
      tableData.push({
        ...assignee,
        uuid: assignee.mapping_id || uid, // Use mapping_id if available, else the generated UUID
      });

      // Update API data
      const existingResourceIndex = apiNewData.findIndex(
        (data) => data.resource_id === assignee.resource_id,
      );

      if (existingResourceIndex === -1) {
        // If resource_id doesn't exist, create a new entry
        apiNewData = [
          ...apiNewData,
          {
            mapping_id: assignee.mapping_id,
            uuid: assignee.mapping_id || uid,
            first_name: assignee.first_name,
            last_name: assignee.last_name,
            profile_image: assignee.profile_image,
            resource_id: assignee.resource_id,
            schedule_times: [
              {
                schedule_uuid: assignee.mapping_id || uid,
                start_time: null,
                end_time: null,
              },
            ],
          },
        ];
      } else {
        // If resource_id exists, add schedule time
        apiNewData = produce(apiNewData, (draft) => {
          draft[existingResourceIndex].schedule_times.push({
            schedule_uuid: assignee.mapping_id || uid,
            start_time: null,
            end_time: null,
          });
        });

        // apiNewData[existingResourceIndex].schedule_times = [
        //   ...apiNewData[existingResourceIndex].schedule_times,
        //   {
        //     schedule_uuid: assignee.mapping_id || uid,
        //     start_time: null,
        //     end_time: null,
        //   },
        // ];
      }
    });

    // Perform state updates outside the loop
    set(assigneesAtom, tableData); // Update assigneesAtom with the new table data
    set(selectedResourceAtom, [...tableData.map((item) => item.resource_id)]);
    set(apiAssigneesAtom, (prev) => ({
      ...prev,
      new: apiNewData, // Update the 'new' field in apiAssigneesAtom
    }));
  },
);

// export const setAssigneeList = atom(
//   null,
//   (get, set, value: IncomingAssigneeTableData[]) => {
//     const tableData: Array<AssigneeTableData> = [];
//     const apiNewData: Array<AssigneeData> = [];
//     value.map((assignee) => {
//       const uid = uuid();
//       tableData.push({
//         ...assignee,
//         uuid: assignee.mapping_id ? assignee.mapping_id : uid,
//       });
//       const newData = produce(apiNewData, (draft) => {
//         const index = draft.findIndex(
//           (data) => data.resource_id === assignee.resource_id
//         );
//         if (index === -1) {
//           draft.push({
//             mapping_id: assignee.mapping_id,
//             uuid: assignee.mapping_id ? assignee.mapping_id : uid,
//             first_name: assignee.first_name,
//             last_name: assignee.last_name,
//             profile_image: assignee.profile_image,
//             resource_id: assignee.resource_id,
//             schedule_times: [
//               {
//                 schedule_uuid: assignee.mapping_id ? assignee.mapping_id : uid,
//                 start_time: null,
//                 end_time: null,
//               },
//             ],
//           });
//         } else {
//           draft[index].schedule_times.push({
//             schedule_uuid: assignee.mapping_id ? assignee.mapping_id : uid,
//             start_time: null,
//             end_time: null,
//           });
//         }
//       });

//       set(assigneesAtom, tableData);

//       set(apiAssigneesAtom, (prev) => {
//         return {
//           ...prev,
//           new: newData,
//         };
//       });
//     });
//   }
// );

export const updateAssignee = atom(
  null,
  (get, set, value: UpdateAssigneeInput) => {
    // Update the assigneesAtom
    set(assigneesAtom, (prev) => {
      const newData = produce(prev, (draft) => {
        const index = draft.findIndex(
          (assignee) => assignee.uuid === value.uuid,
        );
        if (index !== -1) {
          // Safely update the assignee data
          draft[index] = {
            ...draft[index],
            ...value,
          };
        } else {
          console.warn("Assignee not found in the list:", value.uuid);
        }
      });

      return newData;
    });

    // Update the apiAssigneesAtom
    set(apiAssigneesAtom, (prev) => {
      const newData = produce(prev, (draft) => {
        if (value.mapping_id) {
          // draft.updated.push(value);
        } else {
          // Handle case where mapping_id does not exist
          // commenting because bug found when duplicate assignee added
          // const resourceIndex = draft.new.findIndex(
          //   (assignee) => assignee.uuid === value.uuid
          // );
          // duplicate assignee bug fix :- need to check resource id then uid after that in shedule
          const resourceIndex = draft.new.findIndex(
            (assignee) => assignee.resource_id === value.resource_id,
          );
          if (resourceIndex !== -1) {
            const scheduleIndex = draft.new[
              resourceIndex
            ].schedule_times.findIndex(
              (schedule) => schedule.schedule_uuid === value.schedule_uuid,
            );
            if (scheduleIndex !== -1) {
              draft.new[resourceIndex].schedule_times[scheduleIndex] = {
                schedule_uuid: value.schedule_uuid,
                start_time: value.start_time,
                end_time: value.end_time,
              };
            } else {
              console.warn(
                "Schedule not found for the assignee:",
                value.schedule_uuid,
              );
            }
          } else {
            console.warn("Assignee not found in the 'new' list:", value.uuid);
          }
        }
      });
      return newData;
    });
  },
);

// export const updateAssignee = atom(
//   null,
//   (get, set, value: UpdateAssigneeInput) => {
//     set(assigneesAtom, (prev) => {
//       const newData = produce(prev, (draft) => {
//         const index = draft.findIndex(
//           (assignee) => assignee.uuid === value.uuid
//         );
//         draft[index] = value;
//       });
//       return newData;
//     });

//     set(apiAssigneesAtom, (prev) => {
//       const newData = produce(prev, (draft) => {
//         if (value.mapping_id) {
//           // draft.updated.push(value);
//         } else {
//           const resourceIndex = draft.new.findIndex(
//             (assignee) => assignee.uuid === value.uuid
//           );
//           const scheduleIndex = draft.new[
//             resourceIndex
//           ].schedule_times.findIndex(
//             (schedule) => schedule.schedule_uuid === value.schedule_uuid
//           );
//           draft.new[resourceIndex].schedule_times[scheduleIndex] = {
//             schedule_uuid: value.schedule_uuid,
//             start_time: value.start_time,
//             end_time: value.end_time,
//           };
//         }
//       });
//       return newData;
//     });
//   }
// );

export const deleteAssigneeItem = atom(
  null,
  (get, set, value: DeleteAssigneeItemInput) => {
    let hasMultipleSchedules = true;

    // Update apiAssigneesAtom
    set(apiAssigneesAtom, (prev) => {
      const newData = produce(prev, (draft) => {
        const resourceIndex = draft.new.findIndex(
          (assignee) => assignee.resource_id === value.resource_id,
        );

        // Ensure resourceIndex exists
        if (resourceIndex !== -1) {
          const scheduleTimes = draft.new[resourceIndex].schedule_times;

          const scheduleIndex = scheduleTimes.findIndex(
            (schedule) => schedule.schedule_uuid === value.schedule_uuid,
          );

          if (scheduleIndex !== -1) {
            if (scheduleTimes.length === 1) {
              // Remove entire resource if only one schedule exists
              hasMultipleSchedules = false;
              draft.new.splice(resourceIndex, 1);
            } else {
              // Remove only the specific schedule
              scheduleTimes.splice(scheduleIndex, 1);
            }
          } else {
            console.warn("Schedule not found:", value.schedule_uuid);
          }
        } else {
          console.warn("Resource not found:", value.resource_id);
        }
      });
      return newData;
    });

    // Update assigneesAtom
    set(assigneesAtom, (prev) => {
      const newData = produce(prev, (draft) => {
        const index = draft.findIndex(
          (assignee) => assignee.uuid === value.uuid,
        );
        if (index !== -1) {
          draft.splice(index, 1);
        } else {
          console.warn("Assignee not found:", value.uuid);
        }
      });
      return newData;
    });

    // Update selectedResourceAtom if only one schedule exists
    if (!hasMultipleSchedules) {
      set(selectedResourceAtom, (prev) => {
        const newData = produce(prev, (draft) => {
          const index = draft.findIndex(
            (resource) => resource === value.resource_id,
          );
          if (index !== -1) {
            draft.splice(index, 1);
          } else {
            console.warn("Selected resource not found:", value.resource_id);
          }
        });
        return newData;
      });
    }
  },
);

// export const deleteAssigneeItem = atom(
//   null,
//   (get, set, value: DeleteAssigneeItemInput) => {
//     let hasMultipleSchedules = true;
//     set(apiAssigneesAtom, (prev) => {
//       const newData = produce(prev, (draft) => {
//         const resourceIndex = draft.new.findIndex(
//           (assignee) => assignee.resource_id === value.resource_id
//         );
//         const scheduleIndex = draft.new[resourceIndex].schedule_times.findIndex(
//           (schedule) => schedule.schedule_uuid === value.schedule_uuid
//         );

//         if (draft.new[resourceIndex].schedule_times.length === 1) {
//           hasMultipleSchedules = false;
//           draft.new.splice(resourceIndex, 1);
//         } else {
//           draft.new[resourceIndex].schedule_times.splice(scheduleIndex, 1);
//         }
//       });
//       return newData;
//     });
//     set(assigneesAtom, (prev) => {
//       const newData = produce(prev, (draft) => {
//         const index = draft.findIndex(
//           (assignee) => assignee.uuid === value.uuid
//         );
//         draft.splice(index, 1);
//       });
//       return newData;
//     });
//     if (!hasMultipleSchedules) {
//       set(selectedResourceAtom, (prev) => {
//         const newData = produce(prev, (draft) => {
//           const index = draft.findIndex(
//             (resource) => resource === value.resource_id
//           );
//           draft.splice(index, 1);
//         });
//         return newData;
//       });
//     }
//   }
// );

export const cloneSchedule = atom(
  null,
  (get, set, value: CloneScheduleInput) => {
    const data = {
      ...value,
      schedule_uuid: value.uuid,
      start_time: value.start_time,
      end_time: value.end_time,
    };
    set(assigneesAtom, (prev) => {
      const newData = produce(prev, (draft) => {
        draft.splice(value.index, 0, data);
      });
      return newData;
    });
    set(apiAssigneesAtom, (prev) => {
      const newData = produce(prev, (draft) => {
        const index = draft.new.findIndex(
          (assignee) => assignee.resource_id === value.resource_id,
        );
        draft.new[index].schedule_times.push({
          schedule_uuid: value.uuid,
          start_time: value.start_time,
          end_time: value.end_time,
        });
      });
      return newData;
    });
  },
);

export const validateSchedule = atom(null, async (get, set) => {
  const tableData = get(assigneesAtom);
  let isValid = true;
  let newTableData = [];
  newTableData = tableData.map((item) => {
    if (!item.start_time && !item.end_time) {
      return { ...item, errors: { start_time: null, end_time: null } };
    }
    if (item.start_time && !item.end_time) {
      isValid = false;
      return {
        ...item,
        errors: {
          start_time: null,
          end_time: "End time is required",
        },
      };
    }
    if (item.end_time && !item.start_time) {
      isValid = false;
      return {
        ...item,
        errors: {
          start_time: "Start time is required",
          end_time: null,
        },
      };
    }
    if (dayjs(item.end_time).diff(dayjs(item.start_time)) <= 0) {
      isValid = false;
      return {
        ...item,
        errors: {
          start_time: null,
          end_time: "End time should be greater than start time",
        },
      };
    }
    return { ...item, errors: { start_time: null, end_time: null } };
  });

  set(assigneesAtom, newTableData);
  return isValid;
});
